/**
 * Sichtbarkeit der fünf „Studio"-Ziele + des Admin-Reiters im UI (#81, Mig 0099).
 *
 * ⚠️ Das hier ist **Kosmetik, keine Zugriffsgrenze.** Die Routen bleiben für
 * jeden erreichbar, der die URL kennt; die serverseitigen Gates
 * (`app/roles.py::require_module` vor den Modul-Endpunkten, `require_admin` vor
 * den Banner-Endpunkten, das 403 in `app/routers/email_automation.py`) sind
 * davon unberührt. Wer daraus eine Sicherheitsaussage baut, baut sie auf Sand.
 *
 * Warum diese Datei existiert: die Regel wird an MEHREREN Stellen gebraucht
 * (linke Navigation, Mobil-Kacheln, Startseiten-Chips, seit Mig 0095 auch der
 * Admin-Reiter in den Einstellungen). Stünde sie in jeder einzeln, fehlt sie
 * beim nächsten Einbauort — genau so ist der Reviewer am 2026-07-31 auf die
 * ungegateten Startseiten-Chips gestossen. Neue Oberfläche, die eines dieser
 * Ziele anbietet, fragt `useStudioZiele()` und prüft ihr Ziel mit
 * `istStudioZiel()`.
 *
 * ## Was sich mit Mig 0099 geändert hat
 *
 * Vorher hingen ALLE fünf Ziele an einem einzigen Schalter — der Rolle `admin`.
 * „Zugriff auf Video" war damit nur als „Zugriff auf alles, inklusive
 * org-weiter Schreibrechte am Signatur-Banner" zu haben. Jetzt hat jedes Ziel
 * sein eigenes Recht, und die Rolle bleibt, was sie ist: die org-weiten
 * Fähigkeiten.
 *
 * Drei Rechte-Quellen, nicht eine — bewusst, weil es drei verschiedene Fragen
 * sind:
 *
 *  1. **Module** (`/auth/me` → `modules`, `users.modules`): Video, Blog,
 *     Bild-Bibliothek, Claim-Freigaben.
 *  2. **Eigenes Gate** für Email-Automatisierungen: Zugang läuft dort über
 *     Abteilung/Allowlist (`has_email_automation_access`) und ist damit
 *     dynamisch — ein gespiegeltes Modul-Recht wäre eine zweite Tür in
 *     denselben Raum und liefe beim ersten Abteilungswechsel auseinander.
 *     Deshalb fragen wir den dafür gebauten Endpunkt `/email-automation/access`
 *     (der antwortet mit `access:false` statt 403, genau für diesen Zweck).
 *  3. **Rolle** (`admin`) für den Admin-Reiter in den Einstellungen.
 */
import { useEffect, useState } from "react";

import { api } from "./api";
import { useAuth } from "./auth";

/**
 * Die Atlas-Rolle, an der die org-weiten Fähigkeiten hängen (Mig 0095,
 * `users.role`). Sie ist KEIN Sammelschalter für die Studio-Ziele mehr — die
 * haben seit Mig 0099 eigene Rechte. Der Server rechnet die Rolle allerdings in
 * die Modul-Liste ein (`roles.py::modules_of`: Admin bekommt alle), damit das
 * Menü diese Regel nicht ein zweites Mal kennen muss.
 */
export const ADMIN_ROLE = "admin";

/**
 * Die Klasse selbst: die fünf Ziele aus #81. Nach ROUTE geschnitten, nicht nach
 * `UseCase.zone` — in der Zone „studio" liegt auch „Tabellen" (`/ds`,
 * useCases.ts:138-145), und das ist ausdrücklich für alle da.
 */
export const STUDIO_ROUTES = [
  "/email-automation",
  "/claims",
  "/library",
  "/heygen",
  "/blog",
  "/fragebogen",
] as const;

export type StudioRoute = (typeof STUDIO_ROUTES)[number];

/**
 * Route → Modulname in `app/roles.py::MODULES`. Die Namen müssen dort genauso
 * stehen; ein Tippfehler hier blendet ein Ziel dauerhaft aus, das der Server
 * durchlässt.
 *
 * `/email-automation` fehlt mit Absicht — das Ziel hat sein eigenes Gate
 * (s. Kopf dieser Datei).
 */
const MODUL_JE_ROUTE: Partial<Record<StudioRoute, string>> = {
  "/claims": "claims",
  "/library": "library",
  "/heygen": "heygen",
  "/blog": "blog",
  // Die Seite heisst /fragebogen, das Modul heisst `questionnaires` (so steht
  // es in `app/roles.py::MODULES` und im Router-Gate). Route und Modulname
  // müssen hier NICHT gleich lauten — der Server kennt nur den Modulnamen.
  "/fragebogen": "questionnaires",
};

/** Zeigt `to` auf eines der fünf Ziele? Deckt Unterpfade und Query mit ab. */
export function istStudioZiel(to?: string | null): boolean {
  if (!to) return false;
  const pfad = to.split("?")[0].split("#")[0].replace(/\/+$/, "") || "/";
  return STUDIO_ROUTES.some((r) => pfad === r || pfad.startsWith(r + "/"));
}

/**
 * Ist die angemeldete Person Atlas-Admin?
 *
 * Maßgeblich ist die in DIESER Sitzung **bestätigte** Rolle (`confirmedRole`),
 * nicht ein gemerkter Wert: sichtbar wird der Reiter nur, wenn `/auth/me`
 * erfolgreich geantwortet und die Rolle genannt hat. `confirmedRole` wird nie
 * persistiert (auth.tsx) — „ausgeblendet" heisst damit wirklich ausgeblendet
 * und nicht „ausgeblendet, ausser der Browser hat noch einen alten Wert".
 *
 * Bewusst NUR hier streng: `email` behält seine Bedeutung „zuletzt bekannt" für
 * alle anderen Verbraucher. Der Riegel „sich nicht selbst einladen"
 * (CalendarMs.tsx:2949) darf bei einem Serverwackler NICHT ausgehen — Strenge
 * fürs Menü darf keine fremde Regel entwaffnen.
 */
export function useIsAdmin(): boolean {
  const { confirmedRole } = useAuth();
  return (confirmedRole ?? "").trim().toLowerCase() === ADMIN_ROLE;
}

/**
 * Ist EIN Modul freigeschaltet? Dieselbe Strenge wie `useIsAdmin`: es zählt
 * nur, was `/auth/me` in dieser Sitzung bestätigt hat.
 */
export function useHatModul(modul: string): boolean {
  const { confirmedModules } = useAuth();
  return confirmedModules.includes(modul);
}

/**
 * Darf die angemeldete Person die Email-Automatisierungen sehen?
 *
 * Eigener Abruf statt Modul-Liste — Begründung im Kopf dieser Datei. Der
 * Startwert ist `false`: bis der Server geantwortet hat, wird nichts angeboten
 * (im Zweifel NICHT anzeigen). Ein Fehlschlag (401/503/offline) bleibt `false`.
 */
function useEmailAutomationZugang(): boolean {
  const { token } = useAuth();
  const [zugang, setZugang] = useState(false);

  useEffect(() => {
    if (!token) {
      setZugang(false);
      return;
    }
    let abgebrochen = false;
    api
      .get<{ access: boolean }>("/email-automation/access")
      .then((a) => {
        if (!abgebrochen) setZugang(Boolean(a?.access));
      })
      .catch(() => {
        if (!abgebrochen) setZugang(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, [token]);

  return zugang;
}

/** Was von der Studio-Gruppe zu sehen ist — je Ziel eine Antwort. */
export interface StudioSicht extends Record<StudioRoute, boolean> {
  /** Ist überhaupt eines der fünf Ziele sichtbar? Treibt die Überschrift
   *  „Studio" — eine Gruppe mit leerem Inhalt wäre schlechter als keine. */
  irgendeins: boolean;
}

/**
 * Sichtbarkeit aller fünf Studio-Ziele in EINEM Aufruf.
 *
 * Folge fürs Layout: die Einträge erscheinen erst nach der Antwort. Deshalb
 * stehen die Studio-Ziele überall am ENDE ihrer Liste (Navigation,
 * Mobil-Kacheln, Startseiten-Chips) — was danach dazukommt, darf nichts
 * verschieben, was schon gerendert war.
 */
export function useStudioZiele(): StudioSicht {
  const { confirmedModules } = useAuth();
  const emailAutomation = useEmailAutomationZugang();

  const sicht = STUDIO_ROUTES.reduce((acc, route) => {
    const modul = MODUL_JE_ROUTE[route];
    acc[route] = modul ? confirmedModules.includes(modul) : emailAutomation;
    return acc;
  }, {} as Record<StudioRoute, boolean>);

  return {
    ...sicht,
    irgendeins: STUDIO_ROUTES.some((r) => sicht[r]),
  };
}
